import React from 'react';

import styled from 'styled-components';

import { useForwardedRef } from '@adsk/alloy-react-helpers';
import { MagnifyingGlassIcon } from '@adsk/alloy-react-icon';
import Overlay from '@adsk/alloy-react-overlay';
import SearchField from '@adsk/alloy-react-search-field';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

const StyledListItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  white-space: nowrap;
  height: 36px;
  padding: 0 12px;
  cursor: pointer;
  background-color: ${({ highlighted }) =>
    highlighted && theme.colors.charcoal050};
  &:hover {
    background-color: ${theme.colors.charcoal050};
  }
`;

const SuggestionLabel = ({ suggestion = '', keyword = '' }) => {
  const reg = new RegExp(
    `(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
    'gi',
  );

  return (
    <OverflowTooltip
      getTargetProps={() => ({ style: { flex: 'auto', overflow: 'hidden' } })}
    >
      {keyword
        ? suggestion.split(reg).map((partial, index) => (
            <span
              style={{
                ...(partial.toLowerCase() === keyword.toLowerCase()
                  ? { fontWeight: 'bold' }
                  : {}),
              }}
              key={index}
            >
              {partial}
            </span>
          ))
        : suggestion}
    </OverflowTooltip>
  );
};

const SuggestionItem = ({
  suggestion = '',
  keyword = '',
  highlighted = false,
  onClick,
}) => {
  return (
    <StyledListItem highlighted={highlighted} onClick={onClick}>
      <MagnifyingGlassIcon style={{ flex: 'none', marginRight: '8px' }} />
      <SuggestionLabel suggestion={suggestion} keyword={keyword} />
    </StyledListItem>
  );
};

const SearchBox = React.forwardRef(
  (
    {
      value,
      suggestions = [],
      onSearch,
      onSelect,
      onKeyDown,
      container,
      ...rest
    },
    forwardedRef,
  ) => {
    const [showMenu, setShowMenu] = React.useState(false);
    const [highlightIndex, setHighlightIndex] = React.useState(undefined);
    const targetRef = useForwardedRef(forwardedRef);

    const menuWidth = targetRef.current ? targetRef.current.offsetWidth : 0;

    React.useEffect(() => {
      setHighlightIndex(undefined);
    }, [suggestions]);

    const handleSearch = (val, e) => {
      onSearch(val, e);
      setShowMenu(false);
      targetRef.current && targetRef.current.blur();
    };

    const handleSelect = (suggestion, e) => {
      onSelect && onSelect(suggestion, e);
      setShowMenu(false);
      targetRef.current && targetRef.current.blur();
    };

    const handleKeyDown = (event) => {
      // Handle Enter key
      if (event.keyCode === 13) {
        if (highlightIndex !== undefined) {
          handleSelect(suggestions[highlightIndex], event);
          setHighlightIndex(undefined);
        } else {
          handleSearch(value, event);
        }
        return;
      }

      // Return if no suggestions
      if (suggestions.length === 0 || !showMenu) onKeyDown && onKeyDown(event);

      // Handle Key Down / Up for suggestions
      switch (event.keyCode) {
        case 40:
          // Key Down
          setHighlightIndex(
            highlightIndex === undefined
              ? 0
              : highlightIndex + 1 < suggestions.length
                ? highlightIndex + 1
                : 0,
          );
          break;
        case 38:
          // Key Up
          setHighlightIndex(
            highlightIndex === undefined
              ? suggestions.length - 1
              : highlightIndex > 0
                ? highlightIndex - 1
                : suggestions.length - 1,
          );
          break;
        default:
          onKeyDown && onKeyDown(event);
          break;
      }
    };

    return (
      <>
        <Overlay
          container={container}
          show={showMenu && suggestions.length > 0}
          placement="bottom-start"
          target={targetRef}
          onHide={() => setShowMenu(false)}
          renderTarget={({ targetProps }) => (
            <SearchField
              {...rest}
              value={value}
              onFocus={(e) => {
                setShowMenu(true);
                rest.onFocus && rest.onFocus(e);
              }}
              onKeyDown={handleKeyDown}
              {...targetProps}
            />
          )}
          renderOverlay={({ overlayProps, placement }) => (
            <Overlay.Container
              ref={overlayProps.ref}
              style={[
                {
                  width: menuWidth,
                  maxWidth: menuWidth,
                  marginTop: '4px',
                  padding: '2px 0',
                },
                overlayProps.style,
              ]}
              variant={Overlay.VARIANTS.LIGHT}
              placement={placement}
            >
              <>
                {suggestions.map((suggestion, idx) => (
                  <SuggestionItem
                    key={`${idx}-${suggestion}`}
                    suggestion={suggestion}
                    keyword={value}
                    highlighted={idx === highlightIndex}
                    onClick={(e) => handleSelect(suggestion, e)}
                  />
                ))}
              </>
            </Overlay.Container>
          )}
        />
      </>
    );
  },
);

SearchBox.displayName = 'SearchBox';

export default SearchBox;
